import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useCart } from "@/lib/cart";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Store", id: "store" },
  { label: "Blog", id: "blog" },
];

const Navbar = ({ onOpenSearch }) => {
  const [scrolled, setScrolled] = useState(false);
  const { count, openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#FAF5EC]/85 backdrop-blur-xl border-b border-kiki-line"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-2">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-2 shrink-0 min-w-0"
        >
          <img src="/logo.png" alt="Kiki Services logo" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-kiki-line shrink-0" />
          <span className="font-display text-lg sm:text-2xl tracking-tight text-kiki-ink truncate">
            Kiki<span className="text-kiki-gold italic">Services</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <Link
            data-testid="nav-link-about"
            to="/about"
            className="text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit tracking-wide"
          >
            About
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            data-testid="nav-cart-btn"
            onClick={openCart}
            aria-label="Open saved items"
            className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-kiki-line bg-white/70 flex items-center justify-center text-kiki-ink hover:text-rose-500 hover:border-rose-300 transition-colors duration-300"
          >
            <Heart className={`w-4 h-4 transition-colors duration-300 ${count > 0 ? "fill-rose-500 text-rose-500" : ""}`} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-kiki-gold text-[#FAF5EC] text-[9px] sm:text-[10px] font-outfit font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
};

export default Navbar;
